// ResetPasswordSuccessPage.tsx
import React, { useState, useEffect } from 'react';
import { Typography, Stack, Box, CircularProgress } from '@mui/material';
import { ActionButton } from '../ActionButton';
import RegisterLayoutWithoutHeader from '../RegisterLayoutWithoutHeader';
import { useNavigate, useParams } from 'react-router-dom';
import { useDataProvider } from 'react-admin';


interface ResetPasswordSuccessPageProps {
  backgroundColor?: string;
  buttonColor?: string;
}

interface PartnerLoginInfo {
  loginId: string;
  clientId: number;
  name: string;
  companyName: string;
  storeLogoThumbnail: {
    src: string;
  };
  userPoolId: string;
  cognitoClientId: string;
}

const ResetPasswordSuccessPage: React.FC<ResetPasswordSuccessPageProps> = ({
  buttonColor = '#BA7967'
}) => {
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId?: string }>();
  const dataProvider = useDataProvider();
  const [partnerInfo, setPartnerInfo] = useState<PartnerLoginInfo | null>(null);
  const [infoLoading, setInfoLoading] = useState(true);
  const [infoError, setInfoError] = useState(false);

  // テナントIDに基づいてパートナー情報を取得
  useEffect(() => {
    const fetchPartnerInfo = async () => {
      if (!tenantId) {
        setInfoLoading(false);
        return;
      }

      try {
        const response = await dataProvider.getPartnerLoginInfo(tenantId);
        setPartnerInfo(response);
      } catch (err) {
        console.error('Failed to fetch partner info:', err);
        setInfoError(true);
      } finally {
        setInfoLoading(false);
      }
    };

    fetchPartnerInfo();
  }, [tenantId, dataProvider]);

  // パートナー情報のローディング中
  if (infoLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // パートナー情報に基づいてロゴを設定
  const storeLogoThumbnail = partnerInfo?.storeLogoThumbnail?.src;
  const partnerName = partnerInfo?.name;

  return (
    <RegisterLayoutWithoutHeader 
      storeLogoThumbnail={storeLogoThumbnail} 
      inviteSite={partnerName}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Stack
          sx={{ maxWidth: '600px', width: '100%' }}
          spacing={5}
          justifyContent={"center"}
          alignItems={"center"}
          direction={"column"}
        >
          <Typography variant="h5" gutterBottom align="center">
            パスワード変更完了
          </Typography>

          <Typography variant="body1" gutterBottom align="center">
            パスワードの変更が完了しました。
          </Typography>

          <ActionButton
            variant="contained"
            type="submit"
            style={{ backgroundColor: buttonColor }}
            onClick={() => { 
              // テナントIDがある場合はテナント専用ログインページに、ない場合は通常のログインページに戻る
              const currentTenantId = tenantId || localStorage.getItem('currentTenantId');
              navigate(currentTenantId ? `/login/${currentTenantId}` : '/login');
            }}
          >
            ログインページへ戻る
          </ActionButton>
        </Stack>
      </Box>
    </RegisterLayoutWithoutHeader>
  );
};

export default ResetPasswordSuccessPage;
